import { TerrainService } from 'app/map/services/terrain.service';
import { Cesium } from 'app/cesium';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { AssetTypeMobility } from 'app/shared/collections/asset_type';
import { EventRegistry } from 'app/shared/events/event_registry';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { ConnectionStat } from './connectionStat';
import { ConnectionCollection, ConnectionReference } from './connectionCollection';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { AssetTypeCollection } from 'app/shared/collections/asset_type_collection.service';
import { LocationStatCollection } from 'app/shared/collections/location_stat_collection.service';
import { VisibleRadioMapOptions } from 'app/views/view_types/network/network_view_constants';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { InterfaceStatType } from 'app/views/view_types/network/collections/radio_stat';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { ColorRendererService } from 'app/shared/color_renderer.service';
import ColorRenderer from 'app/shared/utilities/color_renderer';
import { ConfigService } from 'app/config/config.service';
import { getChannelForAbsoluteFreq } from 'app/views/view_types/network/features/rf_planning/rf_utils';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';

type connectionPositionTuple = [Cesium.Cartesian3, Cesium.Cartesian3];

const NULL_CHANNEL_COLOR = '#000000';
const SECONDS_THROTTLED_ON_CURSOR = ConfigService.imsBaseTimeResolution;

const enum ConnectionDisplayTypes {
    NONE = 'None',
    RSSI = 'RSSI',
    RSRP = 'RSRP',
    RSRQ = 'RSRQ',
    SNR = 'SNR',
    SINR = 'SINR',
    _2_4_GHZ = '2.4GHz Channel (one radio)',
    _5_GHZ = '5GHz Channel (one radio)',
    COST = 'Cost',
    WIFI_RADIO_TECHNOLOGY = 'WIFI Radio Technology',
    CELLULAR_RADIO_TECHNOLOGY = 'Cellular Radio Technology',
    OTHER_RADIO_TECHNOLOGY = 'Other Radio Technology',
}

// Used for displaying view options.

export const CHANNEL_CUT_OFF_2_4_GHZ: number = 14;
export const CHANNEL_START_2_4_GHZ: number = 1;
export const CHANNEL_CUT_OFF_5_GHZ: number = 196;
export const CHANNEL_START_5_GHZ: number = 32;

export const FREQUENCY_MIN_2_4_GHZ: number = 2412;
export const FREQUENCY_MAX_2_4_GHZ: number = 2484;
export const FREQUENCY_MIN_5_GHZ: number = 4915; // Oddly there's a 4.x Ghz 5 Ghz channel...
export const FREQUENCY_MAX_5_GHZ: number = 5875;

const enum ConnectionSelections {
    SELF = 'self',
    FROM = 'from',
    TO = 'to',
}

type ConnectionSelectionMap = {
    self: boolean;
    from: boolean;
    to: boolean;
};

const enum ConnectionWidths {
    THICK = 'thick',
    MEDIUM = 'medium',
    THIN = 'thin',
}

export class ConnectionService {
    private _connectionCollection: ConnectionCollection;
    private _locationStatCollection: LocationStatCollection;
    private _assetTypeCollection: AssetTypeCollection;
    private _selectionService: SelectionService;
    private _clockHandler: ClockHandler;
    private _assetOptionsService: MapOptionsService;
    private _activeConnectionListeners: EventRegistry;
    private _channel_2_4_ColorRenderer: ColorRenderer | null;
    private _channel_5_ColorRenderer: ColorRenderer;
    private _imsObjectVisibilityService: ImsObjectVisibilityService;

    private readonly _rssiColorRenderer: ColorRenderer | null;
    private readonly _rsrpColorRenderer: ColorRenderer | null;
    private readonly _rsrqColorRenderer: ColorRenderer | null;
    private readonly _snrColorRenderer: ColorRenderer | null;
    private readonly _sinrColorRenderer: ColorRenderer | null;
    private readonly _costColorRenderer: ColorRenderer | null;
    private readonly _terrainService: TerrainService;
    private readonly _assetCollection: AssetCollection;

    private _connectionServiceChangeEvent: CachedEventRegistry;

    constructor(
        connectionCollection: ConnectionCollection,
        locationStatCollection: LocationStatCollection,
        assetTypeCollection: AssetTypeCollection,
        clockHandler: ClockHandler,
        selectionService: SelectionService,
        assetOptionsService: MapOptionsService,
        assetCollection: AssetCollection,
        imsObjectVisibilityService: ImsObjectVisibilityService,
        colorRendererService: ColorRendererService,
        terrainService: TerrainService,
        channel_2_4_ColorRenderer: ColorRenderer,
        channel_5_ColorRenderer: ColorRenderer,
    ) {
        this._assetCollection = assetCollection;
        this._assetTypeCollection = assetTypeCollection;
        this._clockHandler = clockHandler;
        this._selectionService = selectionService;
        this._assetOptionsService = assetOptionsService;
        this._connectionCollection = connectionCollection;
        this._locationStatCollection = locationStatCollection;
        this._selectionService = selectionService;
        this._imsObjectVisibilityService = imsObjectVisibilityService;
        this._terrainService = terrainService;

        this._activeConnectionListeners = new EventRegistry();
        this._registerForOnTickUpdates();
        this._registerForMapOptionChanges();
        this._registerForSelectionChanges();

        this._connectionCollection.onChange(this._updateListeners.bind(this));

        this._channel_2_4_ColorRenderer = channel_2_4_ColorRenderer;
        this._channel_5_ColorRenderer = channel_5_ColorRenderer;

        this._rssiColorRenderer = colorRendererService.colorRendererFor('avg_rssi');
        this._rsrpColorRenderer = colorRendererService.colorRendererFor('avg_rsrp');
        this._rsrqColorRenderer = colorRendererService.colorRendererFor('avg_rsrq');
        this._snrColorRenderer = colorRendererService.colorRendererFor('snr');
        this._sinrColorRenderer = colorRendererService.colorRendererFor('avg_sinr');
        this._costColorRenderer = colorRendererService.colorRendererFor('cost');

        this._connectionServiceChangeEvent = new CachedEventRegistry();
    }

    /**
     * Fires an event when the connection item service is ready
     * @param callback
     * @returns {() => any}
     */
    onConnectionServiceChange(callback) {
        return this._connectionServiceChangeEvent.addEventListener(callback);
    }

    private _registerForMapOptionChanges() {
        this._assetOptionsService.addOnMapOptionsChanged(async () => {
            await this._updateListeners();
        });
    }

    private _registerForOnTickUpdates() {
        this._clockHandler.addOnCursorMovedListener(async () => {
            await this._updateListeners();
        }, SECONDS_THROTTLED_ON_CURSOR);
    }

    private _registerForSelectionChanges() {
        this._selectionService.onSelectionChange(async () => {
            if (VisibleRadioMapOptions.SELECTED === this._getCurrentMapOption()) {
                await this._updateListeners();
            }
        });
    }

    private async _updateListeners() {
        await this._activeConnectionListeners.notifyListeners(this.getCurrentReferences());
    }

    async updateListeners(): Promise<void> {
        return this._updateListeners();
    }

    referenceToId(fromId: number, toId: number): string {
        if (toId) {
            return fromId.toString() + '-' + toId.toString();
        } else {
            return fromId.toString() + '-' + '';
        }
    }

    idToReference(id: string): { fromId: number; toId: number } {
        const ids = id.split('-');
        // If we have an active connection
        if (ids[1] === '') {
            return { fromId: parseInt(ids[0]), toId: undefined };
        }
        // If we have a to ID to parse
        return { fromId: parseInt(ids[0]), toId: parseInt(ids[1]) };
    }

    onChange(callback: (activeConnections: unknown[]) => void): () => void {
        return this._activeConnectionListeners.addEventListener(callback);
    }

    getStat(fromId: number, toId: number, time: number): ConnectionStat {
        return this._connectionCollection.getConnectionStat(fromId, toId, time);
    }

    getSelected(fromId: number, toId: number, time: number): ConnectionSelectionMap {
        const map = {
            self: false,
            to: false,
            from: false,
        };
        const stat = this.getStat(fromId, toId, time);
        if (!stat || !stat.fromAssetId || !stat.toAssetId) {
            return undefined;
        }

        if (
            this._selectionService.isSelected(
                this.referenceToId(fromId, toId),
                SelectionType.CONNECTION,
            )
        ) {
            map[ConnectionSelections.SELF] = true;
        }

        if (this._selectionService.isSelected(stat.toAssetId, SelectionType.ASSET)) {
            map[ConnectionSelections.TO] = true;
        }

        if (this._selectionService.isSelected(stat.fromAssetId, SelectionType.ASSET)) {
            map[ConnectionSelections.FROM] = true;
        }

        return map;
    }

    getConnectionType(fromId: number, toId: number, time: number): ConnectionWidths {
        const stat = this.getStat(fromId, toId, time);
        if (!stat || !stat.fromAssetTypeId || !stat.toAssetTypeId) {
            return undefined;
        }

        const fromAssetType = this._getAssetType(stat.fromAssetTypeId);
        const toAssetType = this._getAssetType(stat.toAssetTypeId);

        // Handle the situation where asset types haven't loaded yet
        if (!fromAssetType || !toAssetType) {
            return undefined;
        }

        const fromMobility = fromAssetType.mobility;
        const toMobility = toAssetType.mobility;
        if (fromMobility === AssetTypeMobility.MOBILE || toMobility === AssetTypeMobility.MOBILE) {
            return ConnectionWidths.THIN;
        } else if (
            fromMobility === AssetTypeMobility.SEMI_MOBILE ||
            toMobility === AssetTypeMobility.SEMI_MOBILE
        ) {
            return ConnectionWidths.MEDIUM;
        }
        return ConnectionWidths.THICK;
    }

    private _getAssetType(assetTypeId: number): any {
        return this._assetTypeCollection.get(assetTypeId);
    }

    private shouldDisplayIpDeviceConnection(fromIpDeviceId: number, time: number): boolean {
        return false;
    }

    getShow(fromId: number, toId: number, time: number): boolean {
        const stat: ConnectionStat = this.getStat(fromId, toId, time);
        const doesConnectionStatExist = stat && stat.toAssetId && stat.fromAssetId;
        const doBothAssetsExistAndShow =
            doesConnectionStatExist &&
            this._imsObjectVisibilityService.isAssetShown(stat.toAssetId) &&
            this._imsObjectVisibilityService.isAssetShown(stat.fromAssetId);

        if (
            !doBothAssetsExistAndShow ||
            !this.shouldDisplayIpDeviceConnection(stat.fromIpDeviceId, time)
        ) {
            return false;
        }

        const displayTypes = this._getDisplayType();
        const wifiDisplayType = displayTypes.wifiConnectionDisplayType || '';
        const cellularDisplayType = displayTypes.cellularConnectionDisplayType || '';
        const otherDisplayType = displayTypes.otherConnectionDisplayType || '';

        //check if channel is a frequency if it is convert to channel
        const channel = getChannelForAbsoluteFreq(stat.channel);

        switch (stat.statType) {
            case InterfaceStatType.WIFI:
                switch (wifiDisplayType) {
                    case ConnectionDisplayTypes._2_4_GHZ:
                        return channel && channel < CHANNEL_CUT_OFF_2_4_GHZ;
                    case ConnectionDisplayTypes._5_GHZ:
                        return channel && channel >= CHANNEL_CUT_OFF_2_4_GHZ;
                    case ConnectionDisplayTypes.NONE:
                        return false;
                    default:
                        return true;
                }
            case InterfaceStatType.LTE:
                switch (cellularDisplayType) {
                    case ConnectionDisplayTypes.NONE:
                        return false;
                    default:
                        return true;
                }
            case InterfaceStatType.PTP:
                switch (otherDisplayType) {
                    case ConnectionDisplayTypes.NONE:
                        return false;
                    default:
                        return true;
                }
            case InterfaceStatType.PTMP:
                switch (otherDisplayType) {
                    case ConnectionDisplayTypes.NONE:
                        return false;
                    default:
                        return true;
                }
            case InterfaceStatType.GENERIC:
                switch (otherDisplayType) {
                    case ConnectionDisplayTypes.NONE:
                        return false;
                    default:
                        return true;
                }
        }
    }

    getColor(fromId: number, toId: number, time: number): Cesium.Color {
        const stat = this.getStat(fromId, toId, time);
        if (!stat) {
            return undefined;
        }

        const displayTypes = this._getDisplayType();
        const displayType = this._getDisplayTypeByStatType(stat.statType, displayTypes);

        if (!displayType) {
            return undefined;
        }

        switch (displayType) {
            case ConnectionDisplayTypes._2_4_GHZ: {
                if (!stat.channel) {
                    // Return the null channel for undefined, null or channel number 0
                    return Cesium.Color.fromCssColorString(NULL_CHANNEL_COLOR);
                }

                //check if channel is a frequency if it is convert to channel
                const channel = getChannelForAbsoluteFreq(stat.channel);

                return this._channel_2_4_ColorRenderer.getColor(channel);
            }
            case ConnectionDisplayTypes._5_GHZ: {
                if (!stat.channel) {
                    // Return the null channel for undefined, null or channel number 0
                    return Cesium.Color.fromCssColorString(NULL_CHANNEL_COLOR);
                }

                //check if channel is a frequency if it is convert to channel
                const channel = getChannelForAbsoluteFreq(stat.channel);

                return this._channel_5_ColorRenderer.getColor(channel);
            }
            case ConnectionDisplayTypes.RSSI:
                return this._rssiColorRenderer.getColor(stat.rssi);

            case ConnectionDisplayTypes.RSRP:
                return this._rsrpColorRenderer.getColor(stat.averageRSRP);

            case ConnectionDisplayTypes.RSRQ:
                return this._rsrqColorRenderer.getColor(stat.averageRSRQ);

            case ConnectionDisplayTypes.SNR:
                return this._snrColorRenderer.getColor(stat.snr);

            case ConnectionDisplayTypes.SINR:
                return this._sinrColorRenderer.getColor(stat.averageSINR);

            case ConnectionDisplayTypes.COST:
                return this._costColorRenderer.getColor(stat.cost);

            case ConnectionDisplayTypes.NONE:
                return undefined; // Should this return an invisible colour?

            default:
                return undefined;
        }
    }

    private _getDisplayTypeByStatType(statType: InterfaceStatType, displayTypes: any): string {
        switch (statType) {
            case InterfaceStatType.WIFI:
                return displayTypes.wifiConnectionDisplayType || '';
            case InterfaceStatType.LTE:
                return displayTypes.cellularConnectionDisplayType || '';
            case InterfaceStatType.GENERIC:
                return displayTypes.otherConnectionDisplayType || '';
            case InterfaceStatType.PTP:
                return displayTypes.otherConnectionDisplayType || '';
            case InterfaceStatType.PTMP:
                return displayTypes.otherConnectionDisplayType || '';
            default:
                console.warn('Unknown stat type: ' + statType);
                return '';
        }
    }

    private _getDisplayType() {
        return this._assetOptionsService.selectedMapOptions || {};
    }

    getPositions(
        fromId: number,
        toId: number,
        julianTime: Cesium.JulianDate,
        unixTime: number,
    ): connectionPositionTuple {
        const stat = this.getStat(fromId, toId, unixTime);

        if (!stat || !stat.fromAssetId || !stat.toAssetId) {
            return undefined;
        }
        const fromPos = this.getPosition(stat.fromAssetId, julianTime);
        if (!fromPos) {
            return undefined;
        }
        const toPos = this.getPosition(stat.toAssetId, julianTime);
        if (!toPos) {
            return undefined;
        }

        return [fromPos, toPos];
    }

    private getPosition(assetId: number, time: Cesium.JulianDate): Cesium.Cartesian3 {
        return this._locationStatCollection.getPositionAtTime(
            assetId,
            time,
            this._terrainService.mapTerrainEnabled,
        );
    }

    getIsConnectedTo(toId: number, time: number): boolean {
        return this._connectionCollection.getIsConnectedTo(toId, time);
    }

    getNextHopReference(fromId: number): ConnectionReference {
        const stat = this.getNextHopStat(fromId);
        return { fromId: stat.fromId, toId: stat.toId };
    }

    getConnectionStat(fromId: number, toId: number): ConnectionStat {
        return this._connectionCollection.getConnectionStat(fromId, toId, this._getCurrentTime());
    }

    getNextHopStat(fromId: number): ConnectionStat {
        return this._connectionCollection.getNextHopStat(fromId, this._getCurrentTime());
    }

    private _getCurrentTime(): number {
        return this._clockHandler.getCursorTimeAsUnix();
    }

    _getRadioIdsFromSelections(): number[] {
        return [];
    }

    _getCurrentMapOption() {
        return this._assetOptionsService.selectedMapOptions.visibleRadiosDisplayType;
    }

    getCurrentReferences(): ConnectionReference[] {
        const option = this._getCurrentMapOption();
        const time = this._getCurrentTime();
        if (!time) {
            return [];
        }

        switch (option) {
            case VisibleRadioMapOptions.NONE:
                return this.getNextHopReferences(time);

            case VisibleRadioMapOptions.ACTIVE:
                return this._connectionCollection.filterWithConnectionReferences(
                    time,
                    (stat: ConnectionStat): boolean => {
                        return stat.isActive;
                    },
                );

            case VisibleRadioMapOptions.STANDBY:
                return this._connectionCollection.filterWithConnectionReferences(
                    time,
                    (stat: ConnectionStat): boolean => {
                        return !stat.isActive;
                    },
                );

            case VisibleRadioMapOptions.INFRA:
                return this._connectionCollection.filterWithConnectionReferences(
                    time,
                    (stat: ConnectionStat): boolean => {
                        return (
                            stat.fromAsset &&
                            stat.fromAsset.type.mobility != AssetTypeMobility.MOBILE
                        );
                    },
                );

            case VisibleRadioMapOptions.MOBILE:
                return this._connectionCollection.filterWithConnectionReferences(
                    time,
                    (stat: ConnectionStat): boolean => {
                        return (
                            stat.fromAsset &&
                            stat.fromAsset.type.mobility == AssetTypeMobility.MOBILE
                        );
                    },
                );

            case VisibleRadioMapOptions.SELECTED:
                const radioIds = this._getRadioIdsFromSelections();
                if (radioIds.length == 0) {
                    return [];
                }
                return this._connectionCollection.mapWithFromRadioIds(radioIds, time);

            case VisibleRadioMapOptions.ALWAYS:
                return this.getConnectionReferences(time);
        }
    }

    getConnectionReferences(time: number): ConnectionReference[] {
        return this._connectionCollection.getCurrentConnectionReferences(time);
    }

    getCurrentConnectionReferences(): ConnectionReference[] {
        return this._connectionCollection.getCurrentConnectionReferences(this._getCurrentTime());
    }

    getNextHopReferences(time: number): ConnectionReference[] {
        return this._connectionCollection.getCurrentNextHopReferences(time);
    }

    getCurrentNextHopReferences(): ConnectionReference[] {
        return this._connectionCollection.getCurrentNextHopReferences(this._getCurrentTime());
    }

    getCurrentNextHopStats(): ConnectionStat[] {
        const t = this._getCurrentTime();
        if (t) {
            return this._connectionCollection.getCurrentNextHopStats(t);
        }
        return [];
    }
}
